import { useRef } from 'react'
import type {
  DeepPartial,
  DefaultValues,
  FieldElement,
  FieldName,
  FieldRefs,
  FieldValues,
  InternalFieldName,
  OmitResetState,
  Ref,
  RegisterOptions,
  SetFieldValue,
  SetValueConfig,
  UnpackNestedValue,
  UseFormMethods,
  UseFormOptions,
} from 'react-hook-form'
import { useForm as useHookForm } from 'react-hook-form'

function isSelect(element: FieldElement): element is HTMLInputElement {
  return !!('dataset' in element && element.dataset?.select)
}

export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext extends Record<string, unknown> = Record<string, unknown>
>(
  options?: UseFormOptions<TFieldValues, TContext>,
): UseFormMethods<TFieldValues> {
  const defaultValuesRef = useRef<DefaultValues<TFieldValues>>(
    (options?.defaultValues || {}) as DefaultValues<TFieldValues>,
  )
  const fieldsRef = useRef<FieldRefs<TFieldValues>>({})
  const methods = useHookForm<TFieldValues, TContext>(options)

  function register<TFieldElement extends FieldElement<TFieldValues>>(
    options?: RegisterOptions,
  ): (ref: (TFieldElement & Ref) | null) => void
  function register(
    name: FieldName<TFieldValues>,
    options?: RegisterOptions,
  ): void
  function register<TFieldElement extends FieldElement<TFieldValues>>(
    ref: (TFieldElement & Ref) | null,
    options?: RegisterOptions,
  ): void
  function register<TFieldElement extends FieldElement<TFieldValues>>(
    ref?:
      | RegisterOptions
      | FieldName<TFieldValues>
      | (TFieldElement & Ref)
      | null,
    options?: RegisterOptions,
  ): ((ref: (TFieldElement & Ref) | null) => void) | void {
    if (ref && typeof ref === 'object' && 'name' in ref && isSelect(ref)) {
      const exists =
        fieldsRef.current[ref.name as InternalFieldName<TFieldValues>]
      fieldsRef.current[ref.name as InternalFieldName<TFieldValues>] = { ref }

      const defaultValue = defaultValuesRef.current[ref.name]

      if (!exists && defaultValue) {
        setValue(ref.name as FieldName<TFieldValues>, defaultValue)
      }
    }

    methods.register(ref as TFieldElement & Ref, options)
  }

  function reset(
    values?: UnpackNestedValue<DeepPartial<TFieldValues>>,
    omitResetState?: OmitResetState,
  ): void {
    fieldsRef.current = {}
    defaultValuesRef.current = { ...(values || defaultValuesRef.current) }

    methods.reset(values, omitResetState)
  }

  function setValue(
    name: FieldName<TFieldValues>,
    value: SetFieldValue<TFieldValues>,
    config?: SetValueConfig,
  ): void {
    const ref = fieldsRef.current[name]?.ref

    if (ref && isSelect(ref)) {
      const descriptor = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      ) as PropertyDescriptor
      const inputEvent = new Event('input', { bubbles: true })

      descriptor.set?.call(ref, value)
      ref.dispatchEvent(inputEvent)
    }

    methods.setValue(name, value, config)
  }

  return {
    ...methods,
    register,
    reset,
    setValue,
  }
}
